import { useState, useCallback, useEffect } from "react";
import DataTable from "aldoo-ra/DataTable";
import Typography from "aldoo-ra/Typography";
import Button from "aldoo-ra/Button";
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { getStoreAccount } from "aldoo-ra/Store/api";
import { url } from "aldoo-ra/CMS/url";
import GridSummary from "aldoo-ra/GridSummary";

// A purchase's price, in the currency it was paid in; without one the number alone
const formatPrice = (price) => {
  const amount = price.amount || 0
  if (!price.currency) return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(amount)
  return new Intl.NumberFormat("en-US", { style: "currency", currency: price.currency }).format(amount)
}

const PurchaseDetailsModal = ({
  purchase,
  onClose,
  texts,
}) => {
  if (!purchase) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4">
      <div className="bg-paper rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
        <div className="pb-7 border-b flex justify-between items-center">
          <Typography
            variant="h4"
            className="my-0"
          >
              {texts?.mobile_dialog_title || "Purchase Details"}
            </Typography>

          <Button variant="text" onClick={onClose}>
            <XMarkIcon className="w-6 h-6" />
          </Button>
        </div>

        <div className="pt-4">
          <GridSummary
            variant="aqua"
            config={{
              layout: [
                {
                  cells: [
                    {
                      type: "custom",
                      label: texts?.product_name || "Product",
                      value: {
                        content: (
                          <div>
                            <div className="flex items-center space-x-3 mb-2">
                              {purchase.thumbnail && (
                                <img 
                                  src={url(purchase.thumbnail)} 
                                  alt={purchase.name || "Product"}
                                  className="w-8 h-8 rounded object-cover flex-shrink-0"
                                />
                              )}
                              <span className="font-medium">
                                {purchase.name || purchase.pid}
                              </span>
                            </div>
                            {purchase.description && (
                              <div className="text-sm text-text_secondary">
                                {purchase.description}
                              </div>
                            )}
                          </div>
                        )
                      },
                      colSpan: 2
                    },
                    {
                      type: "text",
                      label: texts?.platform || "Platform",
                      value: purchase.platform || "N/A",
                      colSpan: 2
                    },
                    {
                      type: "text",
                      label: texts?.date || "Purchase Date",
                      value: moment(purchase.purchase_date).format("MMM D, YYYY")
                    },
                    {
                      type: "text",
                      label: texts?.price || "Price",
                      value: purchase.price ? formatPrice(purchase.price) : "N/A"
                    }
                  ]
                }
              ]
            }}
          />
        </div>
      </div>
    </div>
  );
};

const Purchases = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1080);

  const texts = TextBundle({ id: "aldoo-store" })

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1080);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleView = (purchase) => {
    setSelectedPurchase(purchase);
  };

  // Custom API method that extracts purchases from store account
  const getPurchases = useCallback(async ({ page, itemsPerPage }) => {
    try {
      const accountData = await getStoreAccount();
      if (accountData.error) {
        return { error: accountData.error };
      }
      
      const purchases = accountData.products || [];
      
      // Sort by purchase date (newest first)
      const sortedPurchases = purchases.sort((a, b) => 
        new Date(b.purchase_date) - new Date(a.purchase_date)
      );
      
      // Apply pagination
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedPurchases = sortedPurchases.slice(startIndex, endIndex);
      
      return {
        data: paginatedPurchases,
        total: sortedPurchases.length,
        page,
        totalPages: Math.ceil(sortedPurchases.length / itemsPerPage)
      };
    } catch (error) {
      console.error('Error fetching purchases:', error);
      return { error: 'Failed to fetch purchases' };
    }
  }, []);

  const mobileRenders = [
    {
      id: "product_name",
      title: texts?.purchase_product_name || "Product",
      headerClassName: "w-[150px]",
      render: (value, item) => (
        <div className="flex items-center space-x-1 md:space-x-3">
          {item.thumbnail && (
            <img 
              src={url(item.thumbnail)} 
              alt={item.name}
              className="w-7 md:w-8 h-7 md:h-8 rounded object-cover"
            />
          )}
          <Typography className="font-medium">
            {item.name || item.pid}
          </Typography>
        </div>
      ),
    },
    {
      id: "purchase_date",
      title: texts?.purchase_date || "Date",
      headerClassName: "w-[80px] md:w-[120px]",
      render: (value, item) => (
        <Typography>
          {moment(item.purchase_date).format("MMM D, YYYY")}
        </Typography>
      ),
    },
    {
      id: "view_more",
      title: texts?.purchase_view_more || "View More",
      headerClassName: "w-[80px] md:w-[100px] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center">
          <Button
            variant="text"
            onClick={() => handleView(item)}
          >
            <EyeIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ];

  const desktopRenders = [
    {
      id: "product_name",
      title: texts?.purchase_product_name || "Product",
      headerClassName: "w-[350px]",
      render: (value, item) => (
        <div className="flex items-center space-x-3">
          {item.thumbnail && (
            <img 
              src={url(item.thumbnail)} 
              alt={item.name}
              className="w-10 h-10 rounded object-cover"
            />
          )}
          <div>
            <Typography 
              variant="info_16" 
              className="font-medium my-0"
            >
              {item.name || item.pid}
            </Typography>

            {item.description && (
              <Typography 
                variant="info_16"
                className="text-gray_2 my-0"
              >
                {item.description.substring(0, 50)}...
              </Typography>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "purchase_date",
      title: texts?.purchase_date || "Date",
      headerClassName: "w-[150px]",
      render: (value, item) => (
        <Typography>
          {moment(item.purchase_date).format("MMM D, YYYY")}
        </Typography>
      ),
    },
    {
      id: "price_paid",
      title: texts?.purchase_price || "Price",
      headerClassName: "w-[120px]",
      render: (value, item) => (
        <Typography>
          {item.price ? formatPrice(item.price) : "N/A"}
        </Typography>
      ),
    },
    {
      id: "actions",
      title: texts?.purchase_actions || "Actions",
      headerClassName: "w-[80px] text-center mx-auto",
      render: (value, item) => (
        <div className="flex items-center">
          <Button
            variant="text"
            className="text-center mx-auto"
            onClick={() => handleView(item)}
          >
            <EyeIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full max-w-[554px] lg:max-w-full bg-paper rounded-[24px] shadow-custom_card px-4 xl:px-6 py-9 mx-auto lg:mx-0">
      <DataTable
        variant="aqua_cards"
        preferredHeight="60vh"
        apiMethod={getPurchases}
        itemHeight="h-[50px]"
        headerControls={null}
        searchObject={{}}
        requestFields={{}}
        sort={{ purchase_date: -1 }}
        renders={isMobile ? mobileRenders : desktopRenders}
        refreshKey={refreshKey}
        showFooter={true}
        apiDataField="data"
        minSearchTriggerTime={500}
        styleOverrides={{
          table: "w-full table-fixed"
        }}
        localUserPreferencesKey="account.purchases"
        onDataLoadingElement={
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        }
        noData={
          <div className="flex justify-center items-center min-h-[200px]">
            <Typography variant="body_18">
              {texts?.no_purchases || "You have no purchases yet"}
            </Typography>
          </div>
        }
      />

      {selectedPurchase && (
        <PurchaseDetailsModal
          purchase={selectedPurchase}
          onClose={() => setSelectedPurchase(null)}
          texts={texts}
        />
      )}
    </div>
  );
}

export default Purchases